import { useEffect, useState } from "react";
import * as api from "../utils/api";
import { AppUser } from "../types";

// Состояние и обработчики формы входа/регистрации (экран до авторизации) —
// вынесено из App.tsx, который был перегружен состоянием множества несвязанных
// концернов. Чистая презентация (JSX формы) — в components/AuthPanel.tsx.
export function useAuthForm(
  currentUser: AppUser | null,
  setCurrentUser: (u: AppUser | null) => void,
  onLoginSuccess: () => void,
) {
  const [authMode, setAuthMode] = useState<"login" | "register">("login");
  const [authError, setAuthError] = useState<string | null>(null);
  const [authBanner, setAuthBanner] = useState<{ text: string; kind: "ok" | "error" } | null>(null);
  // После регистрации показываем «проверьте почту» вместо JWT-выдачи.
  const [checkEmail, setCheckEmail] = useState<string | null>(null);
  // Регистрация — личная (#385): человек называет себя, компанию заводит потом в
  // кабинете. Поле `username` осталось только для формы ВХОДА (email или логин).
  const [authForm, setAuthForm] = useState({
    username: "",
    password: "",
    repeatPassword: "",
    email: "",
    fullName: "",
    phone: "",
  });
  // Согласия при регистрации (#387): лицензия, ПДн и передача третьим лицам
  // обязательны, реклама — добровольная (ст. 18 ФЗ «О рекламе» запрещает
  // обусловливать услугу согласием на рекламу).
  const [consents, setConsents] = useState<Record<api.ConsentKind, boolean>>({
    license: false, pdn: false, third_party: false, ads: false,
  });
  const [legalDocs, setLegalDocs] = useState<api.LegalDoc[]>([]);
  const [legalOpen, setLegalOpen] = useState<{ title: string; body: string } | null>(null);
  useEffect(() => {
    if (authMode !== "register" || legalDocs.length) return;
    api.listLegalDocuments().then(setLegalDocs).catch(() => { /* тексты не критичны для формы */ });
  }, [authMode, legalDocs.length]);
  const openLegal = async (slug: string, fallbackTitle: string) => {
    try {
      const d = await api.fetchLegalDocument(slug);
      setLegalOpen({ title: d.title || fallbackTitle, body: d.body || "" });
    } catch (e: any) {
      setLegalOpen({ title: fallbackTitle, body: `Текст временно недоступен: ${e?.message || e}` });
    }
  };
  const consentsOk = consents.license && consents.pdn && consents.third_party;

  const handleAuthSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);
    setAuthBanner(null);

    try {
      if (authMode === "register") {
        if (authForm.password !== authForm.repeatPassword) {
          setAuthError("Пароли не совпадают");
          return;
        }
        const data = await api.register({
          full_name: authForm.fullName,
          email: authForm.email,
          phone: authForm.phone,
          password: authForm.password,
          consents,
        });
        setCheckEmail(data.email || authForm.email.trim());
        setAuthForm(prev => ({ ...prev, password: "", repeatPassword: "" }));
      } else {
        const { user } = await api.login(authForm.username.trim(), authForm.password);
        setCurrentUser(user);
        setAuthForm({ username: "", password: "", repeatPassword: "", email: "", fullName: "", phone: "" });
        onLoginSuccess();
      }
    } catch (err: any) {
      setAuthError(err.message);
    }
  };

  const handleResendConfirmation = async () => {
    const username = authForm.username.trim() || (currentUser?.username ?? "");
    if (!username) {
      setAuthError("Укажите логин для повторной отправки");
      return;
    }
    try {
      await api.resendConfirmation(username);
      setAuthBanner({ text: "Если учётка существует и не подтверждена, письмо отправлено повторно.", kind: "ok" });
      setAuthError(null);
    } catch (err: any) {
      setAuthError(err.message);
    }
  };

  return {
    authMode, setAuthMode, authError, setAuthError, authBanner, setAuthBanner,
    checkEmail, setCheckEmail, authForm, setAuthForm, consents, setConsents,
    legalDocs, legalOpen, setLegalOpen, openLegal, consentsOk,
    handleAuthSubmit, handleResendConfirmation,
  };
}
